import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, 
  Car, 
  Settings, 
  Calendar,
  Clock,
  Users,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import AdminLogin from './components/AdminLogin';
import Dashboard from './components/Dashboard';
import AdminPanel from './components/AdminPanel';
import Metodika from './components/Metodika';
import ThemeSwitcher from './components/ThemeSwitcher';

function App() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Kontrola existujúceho tokenu pri načítaní
  useEffect(() => {
    const checkExistingToken = async () => {
      const token = localStorage.getItem('admin_token');
      const tokenExpires = localStorage.getItem('token_expires');
      
      if (token && tokenExpires) {
        const now = Date.now();
        const expiresAt = parseInt(tokenExpires);
        
        if (now < expiresAt) {
          // Token je stále platný
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          try {
            // Overiť token na serveri
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://sledovanie-aut-api.onrender.com';
            await axios.post(`${API_BASE_URL}/auth/verify`);
            setIsAdmin(true);
          } catch (error) {
            // Token je neplatný, vymazať ho
            handleTokenExpired();
          }
        } else {
          // Token expiroval
          handleTokenExpired();
        }
      }
    };

    checkExistingToken();
  }, []);

  // Načítanie času poslednej aktualizácie
  const loadLastUpdate = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://sledovanie-aut-api.onrender.com';
      const response = await axios.get(`${API_BASE_URL}/last-update`);
      setLastUpdate(response.data);
    } catch (error) {
      console.error('Chyba pri načítaní času poslednej aktualizácie:', error);
    }
  };

  // Načítanie času poslednej aktualizácie pri spustení
  useEffect(() => {
    loadLastUpdate();
    // Aktualizácia každých 5 minút
    const interval = setInterval(loadLastUpdate, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleTokenExpired = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('token_expires');
    delete axios.defaults.headers.common['Authorization'];
    setIsAdmin(false);
  };

  const handleAdminLogin = () => {
    setIsAdmin(true);
    setShowAdminLogin(false);
    setActiveView('admin');
  };

  const handleAdminLogout = () => {
    handleTokenExpired();
    setActiveView('dashboard');
  };

  // Auto logout pri expirácii tokenu
  useEffect(() => {
    const interval = setInterval(() => {
      const tokenExpires = localStorage.getItem('token_expires');
      if (tokenExpires && Date.now() >= parseInt(tokenExpires)) {
        handleTokenExpired();
      }
    }, 60000); // Kontrola každú minútu

    return () => clearInterval(interval);
  }, []);

  // Interceptor pre axios pre handling 401 errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && isAdmin) {
          handleTokenExpired();
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [isAdmin]);

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      section: 'main'
    },
    {
      id: 'reports',
      label: 'Reporty',
      icon: Calendar,
      section: 'main'
    },
    {
      id: 'metodika',
      label: 'Metodika',
      icon: Car,
      section: 'main'
    }
  ];

  const adminItems = [
    {
      id: 'admin',
      label: 'Správa systému',
      icon: Settings,
      section: 'admin'
    }
  ];

  const renderContent = () => {
    switch (activeView) {
      case 'admin':
        return <AdminPanel onLogout={handleAdminLogout} />;
      case 'reports':
        return <Dashboard viewType="reports" />;
      case 'metodika':
        return <Metodika />;
      default:
        return <Dashboard viewType="overview" />;
    }
  };

  const getPageTitle = () => {
    switch (activeView) {
      case 'admin':
        return { title: 'Správa systému', subtitle: 'Nahrávanie súborov a správa databázy' };
      case 'reports':
        return { title: 'Reporty', subtitle: 'Exporty a prehľady údajov' };
      case 'metodika':
        return { title: 'Metodika', subtitle: 'Technická dokumentácia systému' };
      default:
        return { title: 'Dashboard', subtitle: 'Prehľad štatistík vozidiel' };
    }
  };

  return (
    <div className="app-layout">
      {/* Sidebar overlay for mobile */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <div className="logo-icon">
              <Car size={20} />
            </div>
            <span>Sledovanie Áut</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">Hlavné</div>
            {navigationItems.map((item) => (
              <button
                key={item.id}
                className={`nav-item ${activeView === item.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveView(item.id);
                  setSidebarOpen(false);
                }}
              >
                <item.icon className="nav-item-icon" size={20} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {isAdmin && (
            <div className="nav-section">
              <div className="nav-section-title">Administrácia</div>
              {adminItems.map((item) => (
                <button
                  key={item.id}
                  className={`nav-item ${activeView === item.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveView(item.id);
                    setSidebarOpen(false);
                  }}
                >
                  <item.icon className="nav-item-icon" size={20} />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">
              {isAdmin ? 'A' : 'U'}
            </div>
            <div className="user-info">
              <div className="user-name">{isAdmin ? 'Administrátor' : 'Používateľ'}</div>
              <div className="user-role">{isAdmin ? 'Admin prístup' : 'Verejný prístup'}</div>
            </div>
          </div>
          
          {isAdmin && (
            <button className="nav-item" onClick={handleAdminLogout}>
              <LogOut className="nav-item-icon" size={20} />
              <span>Odhlásiť sa</span>
            </button>
          )}
          
          {!isAdmin && (
            <button className="nav-item" onClick={() => setShowAdminLogin(true)}>
              <Users className="nav-item-icon" size={20} />
              <span>Admin prístup</span>
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="main-content">
        <header className="main-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', width: '100%' }}>
            <button
              className="mobile-menu-button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Otvoriť menu"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <div style={{ flex: 1 }}>
              <div className="page-title">{getPageTitle().title}</div>
              <div className="page-subtitle">{getPageTitle().subtitle}</div>
            </div>
          </div>
          
          <div className="header-actions">
            <ThemeSwitcher />
            <div className="flex items-center gap-2 text-sm text-gray-500 mobile-time-display">
              <Clock size={14} />
              <span className="time-text">
                Posledná aktualizácia: {lastUpdate ? lastUpdate.last_update_formatted : 'Načítava sa...'}
              </span>
              <span className="time-short">
                {lastUpdate ? lastUpdate.last_update_formatted.split(' ')[1] : '--:--:--'}
              </span>
            </div>
          </div>
        </header>

        <main className="main-body">
          {renderContent()}
        </main>
      </div>

      {/* Admin Login Modal */}
      {showAdminLogin && !isAdmin && (
        <AdminLogin 
          onLogin={handleAdminLogin}
          onClose={() => setShowAdminLogin(false)}
        />
      )}
    </div>
  );
}

export default App;